import _ from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchHolterEvents,
  calculateStartStopStripMomentOfListEvent,
  calculateStartStopStripMomentOfListEventV2,
  handleAddEventOthersV2,
  formatEventStrip,
} from '@/entities/event';
import { AIECGViewer, CAPTURE_MODE, ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import {
  EMITTER_CONSTANTS,
  KEY_RECOIL,
  OTHER_EVENT_LIST,
  TYPE_BEAT_ENUM,
  TYPE_EVENT_OTHER,
  TYPE_RHYTHM_EVENT_ENUM,
} from '@/shared/config';
import { useDeepCompareEffect, useEmitter, useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  checkAndProcessBeatData,
  countBeatChannels,
  generateUpdatedBeatsV2,
  handleUpdateBeatChanges,
} from '@/entities/beat';
import { checkAndProcessEcgData, formatDrawDisplay, formatDrawMutation } from '@/entities/ecg';
import { logError } from '@/shared/lib/apiHelpers';
import { isRangesOverlap } from '@/shared/lib/timeRange';
import {
  selectAiChannel,
  selectChartHrsData,
  selectEcgObject,
  selectIsActiveTab,
  selectJumpPortion,
  selectJumpTime,
  selectPickDateAction,
  selectPickedBookmarkDate,
  selectPickedEndDate,
  selectPickedStartDate,
  selectReloadEcgViewer,
  selectIsLoadingStrip,
  selectSelectedColumnInfo,
  selectSelectedEventIndex,
  selectPrevColumnInfo,
  selectNextColumnInfo,
  selectMinuteInfoEvents,
  selectBeatChanges,
  selectEventChanges,
  selectEventOthers,
  selectSelectedEvent,
  selectBulkBeatsChanges,
  selectResetBulkBeatsChanges,
  useReportStore,
} from '@/entities/report';
import { TPickDateAction } from '@/shared/model';
import { getUpdatedBeatMarkerV2, handleResetAppliedBuldBeats } from '@/widgets/beats/ui/helper';
import type { EventEcgViewerWrapperProps } from './eventEcgViewerWrapper';

/**
 * Drives the ECG viewer panel of the Beats tab, loading the waveform, beats and overlapping events for the selected minute and exposing the handlers its chart needs. Binds the TAB_1 report-store slices for channel, jump time, picked dates and beat changes, downloads and trims ECG and holter beat data whenever the selected column changes, rebuilds the viewer object on beat, bookmark, config or tab changes, and listens for Escape while a date is being picked.
 *
 * @param props - The wrapper props carrying studyId, profileId, the ECG data map, timezone offset and the arrow-button handler.
 * @returns Viewer state including the loading flags, ECG object, chart and strip-epoch refs, AI channel, arrow-disabled flags, pick-date state, event and bookmark indicators, the selected event, and the capture, pick-date, arrow and beat-update handlers.
 */
const useEventEcgViewerWrapper = ({
  ecgDataMap,
  profileId,
  studyId,
  timezoneOffset,
  handleClickArrowButton,
}: EventEcgViewerWrapperProps) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const selectedColumnInfo = useReportStore(selectSelectedColumnInfo(keyRecoil));
  const selectedEventIndex = useReportStore(selectSelectedEventIndex(keyRecoil));
  const prevColumnInfo = useReportStore(selectPrevColumnInfo(keyRecoil));
  const nextColumnInfo = useReportStore(selectNextColumnInfo(keyRecoil));
  const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
  /**
   * Stores the pending beat edits for the Beats tab. Forwards the value to the report store's setBeatChanges action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setBeatChanges = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(keyRecoil, v);
  /**
   * Reads the pending beat edits recorded for the Beats tab. Applies the TAB_1 beatChanges selector to a fresh report-store snapshot rather than subscribing to it.
   *
   * @returns The list of pending beat changes for the tab.
   */
  const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
  /**
   * Reads the pending event edits recorded for the Beats tab. Applies the TAB_1 eventChanges selector to a fresh report-store snapshot rather than subscribing to it.
   *
   * @returns The list of edited events for the tab.
   */
  const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
  const pickDateAction = useReportStore(selectPickDateAction(keyRecoil));
  const setPickDateActionAction = useReportStore((s) => s.actions.setPickDateAction);
  /**
   * Stores which date the technician is currently picking on the strip, or none. Forwards the value to the report store's setPickDateAction action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setPickDateAction = (v: Parameters<typeof setPickDateActionAction>[1]) =>
    setPickDateActionAction(keyRecoil, v);
  const pickedStartDate = useReportStore(selectPickedStartDate(keyRecoil));
  const setPickedStartDateAction = useReportStore((s) => s.actions.setPickedStartDate);
  /**
   * Stores the start timestamp picked on the ECG strip. Forwards the value to the report store's setPickedStartDate action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setPickedStartDate = (v: Parameters<typeof setPickedStartDateAction>[1]) =>
    setPickedStartDateAction(keyRecoil, v);
  const pickedEndDate = useReportStore(selectPickedEndDate(keyRecoil));
  const setPickedEndDateAction = useReportStore((s) => s.actions.setPickedEndDate);
  /**
   * Stores the end timestamp picked on the ECG strip. Forwards the value to the report store's setPickedEndDate action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setPickedEndDate = (v: Parameters<typeof setPickedEndDateAction>[1]) =>
    setPickedEndDateAction(keyRecoil, v);
  const pickedBookmarkDate = useReportStore(selectPickedBookmarkDate(keyRecoil));
  const setPickedBookmarkDateAction = useReportStore((s) => s.actions.setPickedBookmarkDate);
  /**
   * Stores the bookmark timestamp picked on the ECG strip. Forwards the value to the report store's setPickedBookmarkDate action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setPickedBookmarkDate = (v: Parameters<typeof setPickedBookmarkDateAction>[1]) =>
    setPickedBookmarkDateAction(keyRecoil, v);
  const aiChannel = useReportStore(selectAiChannel(keyRecoil));
  const setAiChannelAction = useReportStore((s) => s.actions.setAiChannel);
  /**
   * Stores the AI beat channel derived for the current strip. Forwards the value to the report store's setAiChannel action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setAiChannel = (v: Parameters<typeof setAiChannelAction>[1]) =>
    setAiChannelAction(keyRecoil, v);
  const reloadEcgViewer = useReportStore(selectReloadEcgViewer(keyRecoil));
  const ecgObject = useReportStore(selectEcgObject(keyRecoil));
  const setEcgObjectAction = useReportStore((s) => s.actions.setEcgObject);
  /**
   * Stores the ECG viewer object the chart renders. Forwards the value to the report store's setEcgObject action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setEcgObject = (v: Parameters<typeof setEcgObjectAction>[1]) =>
    setEcgObjectAction(keyRecoil, v);
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));
  /**
   * Reads the per-minute heart-rate chart data backing the Beats tab. Applies the TAB_1 chartHrsData selector to a fresh report-store snapshot rather than subscribing to it.
   *
   * @returns The heart-rate chart points for the tab.
   */
  const getChartHrsData = () => selectChartHrsData(keyRecoil)(useReportStore.getState());
  /**
   * Reads the other-event strips captured on the Beats tab. Applies the TAB_1 eventOthers selector to a fresh report-store snapshot rather than subscribing to it.
   *
   * @returns The other-event strips for the tab.
   */
  const getEventOthers = () => selectEventOthers(keyRecoil)(useReportStore.getState());
  const eventOthers = useReportStore(selectEventOthers(keyRecoil));
  const setEventOthersAction = useReportStore((s) => s.actions.setEventOthers);
  /**
   * Stores the other-event strips captured on the Beats tab. Forwards the value to the report store's setEventOthers action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setEventOthers = (v: Parameters<typeof setEventOthersAction>[1]) =>
    setEventOthersAction(keyRecoil, v);
  const jumpTime = useReportStore(selectJumpTime(keyRecoil));
  const setJumpTimeAction = useReportStore((s) => s.actions.setJumpTime);
  /**
   * Stores the timestamp the ECG chart should jump to. Forwards the value to the report store's setJumpTime action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setJumpTime = (v: Parameters<typeof setJumpTimeAction>[1]) =>
    setJumpTimeAction(keyRecoil, v);
  /**
   * Reads the horizontal portion offset used when the ECG chart jumps to a time. Applies the TAB_1 jumpPortion selector to a fresh report-store snapshot rather than subscribing to it.
   *
   * @returns The jump portion offset.
   */
  const getJumpPortion = () => selectJumpPortion(keyRecoil)(useReportStore.getState());
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Stores the portion offset applied when the chart jumps to a time. Forwards the value to the report store's setJumpPortion action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const setSelectedEventInfoAction = useReportStore((s) => s.actions.setSelectedEventInfo);
  /**
   * Stores the events overlapping the current strip, in selection order. Forwards the value to the report store's setSelectedEventInfo action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setSelectedEventInfo = (v: Parameters<typeof setSelectedEventInfoAction>[1]) =>
    setSelectedEventInfoAction(keyRecoil, v);
  const setIsLoadingEcgViewerAction = useReportStore((s) => s.actions.setIsLoadingEcgViewer);
  /**
   * Toggles the loading flag that covers the ECG viewer while strip data is downloaded. Forwards the value to the report store's setIsLoadingEcgViewer action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setIsLoadingEcgViewer = (v: Parameters<typeof setIsLoadingEcgViewerAction>[1]) =>
    setIsLoadingEcgViewerAction(keyRecoil, v);
  const setMinuteInfoEventsAction = useReportStore((s) => s.actions.setMinuteInfoEvents);
  /**
   * Stores the minute-info events shown for the current strip. Forwards the value to the report store's setMinuteInfoEvents action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setMinuteInfoEvents = (v: Parameters<typeof setMinuteInfoEventsAction>[1]) =>
    setMinuteInfoEventsAction(keyRecoil, v);
  /**
   * Reads the minute-info events shown alongside the current strip on the Beats tab. Applies the TAB_1 minuteInfoEvents selector to a fresh report-store snapshot rather than subscribing to it.
   *
   * @returns The minute-info events for the tab.
   */
  const getMinuteInfoEvents = () => selectMinuteInfoEvents(keyRecoil)(useReportStore.getState());
  const ecgBookmarks = useReportStore((s) => s.ecgBookmarks);
  // selectedEventState (Recoil/index.ts, Task 7) is typed Array<IEvent | IPoint>, but every
  // consumer here (and elsewhere in this slice) treats it as a single event object - Task 7 atom-type gap.
  const selectedEvent = useReportStore(selectSelectedEvent(keyRecoil)) as any;
  /**
   * Reads the event currently selected on the Beats tab. Applies the TAB_1 selectedEvent selector to a fresh report-store snapshot and casts it, because the slice is declared as an array yet every consumer here treats it as a single event object.
   *
   * @returns The selected event object.
   */
  const getSelectedEvent = () =>
    selectSelectedEvent(keyRecoil)(useReportStore.getState()) as unknown as any;
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Stores the event-related options snapshot the undo and redo stack reads. Forwards the value to the report store's setEventOptions action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const beatChanges = useReportStore(selectBeatChanges(keyRecoil));
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);
  /**
   * Reads the bulk beat reclassification currently applied on the Beats tab. Applies the TAB_1 bulkBeatsChanges selector to a fresh report-store snapshot rather than subscribing to it.
   *
   * @returns The applied bulk beat changes for the tab.
   */
  const getBulkBeatsChanges = () => selectBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  /**
   * Reads the record of applied bulk beat changes that have since been reset on the Beats tab. Applies the TAB_1 resetBulkBeatsChanges selector to a fresh report-store snapshot rather than subscribing to it.
   *
   * @returns The reset bulk beat changes for the tab.
   */
  const getResetBulkBeatsChanges = () =>
    selectResetBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Stores the beat-related options snapshot the undo and redo stack reads. Forwards the value to the report store's setBeatOptions action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(keyRecoil, v);
  const setResetBulkBeatsChangesAction = useReportStore((s) => s.actions.setResetBulkBeatsChanges);
  /**
   * Stores which applied bulk beat changes have since been reset. Forwards the value to the report store's setResetBulkBeatsChanges action, bound to the Beats tab key.
   *
   * @param v - The new value, or an updater function receiving the current one.
   * @returns Nothing.
   */
  const setResetBulkBeatsChanges = (v: Parameters<typeof setResetBulkBeatsChangesAction>[1]) =>
    setResetBulkBeatsChangesAction(keyRecoil, v);

  const [isLoading, setIsLoading] = useState(false);
  const [isDisablePrevBtn, setIsDisablePrevBtn] = useState(false);
  const [isDisableNextBtn, setIsDisableNextBtn] = useState(false);
  const ecgChartRef = useRef<AIECGViewer>(null);
  const startStripEpoch = useRef(null);
  const stopStripEpoch = useRef(null);
  const originalBeatMarkers = useRef([]);
  const originalHesBeatStatus = useRef([]);
  const originalBeatHeatMapIds = useRef([]);
  const originalBeatTemplateIds = useRef([]);
  const beatMarkersInEcgViewer = useRef([]);
  const hesBeatStatusInEcgViewer = useRef([]);
  const isJumpToEnd = useRef(false);
  const isJumpToStart = useRef(false);
  const savedEvents = useRef([]);
  const ecgObjectRef = useRef({});
  const currentBeatsChangeRef = useRef<any>();
  const currentEcgViewerConfig = useRef({ ...ECG_VIEWER_CONFIG_STORE });

  //* prevent api race condition by not setState data of old selected strip or event
  let isActiveQuery: boolean;
  const isPickingDate =
    pickDateAction === TPickDateAction.START ||
    pickDateAction === TPickDateAction.END ||
    pickDateAction === TPickDateAction.BOOKMARK;

  const getEventInfoInEcgViewer = ({
    event,
    isSelected = false,
  }: {
    event: any;
    isSelected?: boolean;
    selectedStrip?: any;
  }) => {
    const { samplingFrequency } = ecgDataMap;
    const isEvent = event.start && event.stop; // not minute info (minute info dont have start and stop)
    let startPos = 0;
    let stopPos = 0;
    let color;
    if (isEvent) {
      const isArtifactEvent = event.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT;
      const isAfibEvent = event.type === TYPE_RHYTHM_EVENT_ENUM.AFIB;
      color = isArtifactEvent ? 'gray' : isAfibEvent ? 'orange' : 'red';
      const startEpoch = new Date(event.start).getTime();
      const stopEpoch = new Date(event.stop).getTime();
      if (startStripEpoch.current <= startEpoch) {
        const eventDiffSeconds = Math.abs(startEpoch - startStripEpoch.current) / 1000;
        startPos = Math.round(eventDiffSeconds * samplingFrequency);
      }
      if (stopEpoch >= stopStripEpoch.current) {
        stopPos = (stopStripEpoch.current - startStripEpoch.current) / 1000;
      } else {
        stopPos = (stopEpoch - startStripEpoch.current) / 1000;
      }
      stopPos = Math.round(stopPos * samplingFrequency);
    }
    return {
      startPos,
      stopPos,
      color,
      id: event.id,
      isSelected,
    };
  };

  const createObjectEcg = ({
    ecg,
    beats,
    hesBeatStatus,
    events,
    eventIndex,
    beatHeatMapIds,
    exportEcgData,
    beatTemplateIds,
  }: {
    ecg: any;
    beats: any;
    hesBeatStatus: any;
    events: any;
    eventIndex: any;
    beatHeatMapIds: any;
    exportEcgData: any;
    beatTemplateIds: any;
  }) => {
    const { channels, gain, samplingFrequency } = ecgDataMap;
    const eventsInfo = _.map(events, (event, index) =>
      getEventInfoInEcgViewer({ event, isSelected: index === eventIndex }),
    );
    // *: Restore data from previous editing session
    let drawStartSample = 0;
    const eventSelected = getSelectedEvent() as any;
    const findEvent = _.find(events, (x: any) => x.idEvent === eventSelected?.idEvent);
    let drawList: any = [];
    if (findEvent?.type) {
      drawList = findEvent?.draw;
    } else {
      const otherEvents = getEventOthers() as any[];
      const filterRemoveOther = _.filter(otherEvents, (x) => !x.isRemovedStrip);
      drawList =
        _.filter(_.flatten(_.map(filterRemoveOther, (event) => event.draw)), (x) => !!x) || [];
    }
    const { draw, startSample } = formatDrawDisplay(
      drawList,
      startStripEpoch.current,
      samplingFrequency,
      stopStripEpoch.current,
    );
    if (startSample) {
      drawStartSample = startSample;
    }
    const viewerObject = {
      options: {},
      widgets: draw,
    };
    savedEvents.current = _.cloneDeep(events);
    const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
      beats,
      hesBeatStatus,
      beatChanges: getBeatChanges(),
      startEpoch: startStripEpoch.current,
      stopEpoch: stopStripEpoch.current,
      beatHeatMapIds,
      beatTemplateIds,
      applyBulkBeatsChanges: getBulkBeatsChanges(),
      resetAppliedBulkBeatsChanges: getResetBulkBeatsChanges(),
    });
    beatMarkersInEcgViewer.current = updatedBeats;
    hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;
    currentEcgViewerConfig.current = { ...ECG_VIEWER_CONFIG_STORE };
    const endStripPos =
      ((stopStripEpoch.current - startStripEpoch.current) / 1000) * samplingFrequency;
    const newEcgObject = {
      dataSamples: ecg,
      adcGain: gain,
      adcZero: 0,
      numChannels: channels.length,
      diagnosticLead: 2,
      samplingRate: samplingFrequency,
      events: eventsInfo,
      viewerObject,
      // *: Temporarily adding "aiPredict" to display beat markers from server
      aiPredict: [
        {
          beats: updatedBeats,
          hesBeatStatus: updatedHesBeatStatus,
          channel: 1,
          hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
        },
      ],
      isAppliedAIByDefault: true,
      isShowVES: true,
      isShowSVES: true,
      isShowBeatMarkers: true,
      jumpOffset: isJumpToStart.current ? 1 : isJumpToEnd.current ? endStripPos : drawStartSample,
      hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
      hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
      notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
      speed: ECG_VIEWER_CONFIG_STORE.spdValue,
      amp: ECG_VIEWER_CONFIG_STORE.ampValue,
      wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
      exportEcgData,
    };
    setEcgObject(newEcgObject);
    ecgObjectRef.current = newEcgObject;
  };

  /**
   * Loads the ECG waveform, holter beats and overlapping other-events for the selected minute of the Beats tab and builds the viewer object from them. Fetches the hour's other-events, ECG samples and beat data in parallel, merges overlapping neighbour-column and artifact events, trims samples and beats to the computed strip window, derives the AI channel, disables the prev or next arrow when the adjacent minute has no average heart rate, and logs and clears loading on failure.
   *
   * @param selectedEvents - The events of the selected column, with the minute info last.
   * @param eventIndex - Index within the list of the event to mark as selected in the viewer.
   * @returns A promise resolving once the viewer object for the minute is built and the loading flags are cleared.
   */
  const downloadDataEcgV2 = async (selectedEvents: any[], eventIndex = 0) => {
    if (_.isEmpty(selectedEvents)) {
      return;
    }
    setIsLoading(true);
    setIsLoadingEcgViewer(true);
    try {
      const events = [...selectedEvents];
      const minuteInfo = events[events.length - 1];
      const { defaultStartStrip, defaultStopStrip } = calculateStartStopStripMomentOfListEventV2({
        startTimeEpoch: minuteInfo.startTimeEpoch,
      });
      const startHours = moment(minuteInfo.startTimeEpoch).startOf('hours');
      const startSearchDate = startHours.toISOString();
      const stopSearchDate = startHours.clone().add(1, 'hours').toISOString();
      const { events: downloadEvents } = await fetchHolterEvents(
        {
          studyId,
          profileId,
          types: [TYPE_EVENT_OTHER],
          startSearchDate,
          stopSearchDate,
        },
        0,
        true,
        null,
        false,
      );

      const [ecgData, holterBeats] = await Promise.all([
        checkAndProcessEcgData({
          ecgDataMap,
          dataLength:
            ((defaultStopStrip.valueOf() - defaultStartStrip.valueOf()) / 1000) *
            ecgDataMap.samplingFrequency,
          // checkAndProcessEcgData's momentObject type (common/handlerEcgData.ts, Task 7) declares
          // string | number, but the function itself does moment(momentObject.startMoment) - it accepts Moment too.
          momentObject: {
            startMoment: defaultStartStrip,
            stopMoment: defaultStopStrip,
          } as any,
        }),
        checkAndProcessBeatData({
          ecgDataMap,
          momentObject: {
            startMoment: defaultStartStrip,
            stopMoment: defaultStopStrip,
          } as any,
        }),
      ]);

      if (isActiveQuery) {
        const downloadEventsFormat = formatEventStrip(downloadEvents);
        const startMinuteString = moment(minuteInfo.startTimeEpoch).toISOString();
        const stopMinuteString = moment(minuteInfo.startTimeEpoch).add(60, 'seconds').toISOString();
        _.forEach(downloadEventsFormat, (downloadEvent: any) => {
          // isRangesOverlap (handler.ts, Task 7) declares number params but every call site here
          // passes ISO date strings, which compare correctly lexicographically - pre-existing.
          if (
            isRangesOverlap(
              downloadEvent.start,
              downloadEvent.stop,
              startMinuteString as any,
              stopMinuteString as any,
            )
          ) {
            downloadEvent.startTimeEpoch = minuteInfo.startTimeEpoch;
            events.push(downloadEvent);
          }
        });
        const { startStrip, stopStrip } = calculateStartStopStripMomentOfListEvent({
          events,
        });
        const startTimeString = startStrip.toISOString();
        const stopTimeString = stopStrip.toISOString();
        _.forEach(nextColumnInfo, (event: any) => {
          if (
            !_.find(events, (x) => x.idEvent === event.idEvent) &&
            isRangesOverlap(event.start, event.stop, startTimeString as any, stopTimeString as any)
          ) {
            events.push(event);
          }
        });
        _.forEach(prevColumnInfo, (event: any) => {
          if (
            !_.find(events, (x) => x.idEvent === event.idEvent) &&
            isRangesOverlap(event.start, event.stop, startTimeString as any, stopTimeString as any)
          ) {
            events.push(event);
          }
        });
        const eventChange = getEventChanges();
        const eventChanges = _.filter(eventChange, (event) => {
          const epochStart = moment(event.start).valueOf();
          const epochStop = moment(event.stop).valueOf();
          const startMinute = minuteInfo.startTimeEpoch;
          const endMinute = minuteInfo.startTimeEpoch + 60000;
          const isInMinute =
            (epochStart >= startMinute && epochStart <= endMinute) ||
            (epochStop >= startMinute && epochStop <= endMinute) ||
            (epochStart <= startMinute && epochStop >= endMinute);
          return event.oldType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT && isInMinute;
        });
        const sortedSelectedEventInfo = _.sortBy(_.cloneDeep([...events, ...eventChanges]), [
          (event) => event.type !== TYPE_RHYTHM_EVENT_ENUM.ARTIFACT,
          (event) => event?.start,
          (event) => event.type !== TYPE_RHYTHM_EVENT_ENUM.AFIB,
          (event) => !OTHER_EVENT_LIST.includes(event.type),
        ]);
        setSelectedEventInfo(
          _.uniqBy(
            _.filter(sortedSelectedEventInfo, (x) => !x.isNew && x.type !== TYPE_EVENT_OTHER),
            'idEvent',
          ),
        );
        const otherEvents = _.filter(sortedSelectedEventInfo, (x) => x.type === TYPE_EVENT_OTHER);
        const minuteInfoEvs = _.uniqBy([...eventOthers, ...otherEvents], 'id');
        setMinuteInfoEvents(minuteInfoEvs);

        startStripEpoch.current = startStrip.valueOf();
        stopStripEpoch.current = stopStrip.valueOf();
        // * Cut ecgdata and beat in range
        const cuttedMsStart = startStripEpoch.current - defaultStartStrip.valueOf();
        const cuttedMsStop = defaultStopStrip.valueOf() - stopStripEpoch.current;
        const cuttedSampleStart = (cuttedMsStart / 1000) * ecgDataMap.samplingFrequency;
        const cuttedSampleStop = (cuttedMsStop / 1000) * ecgDataMap.samplingFrequency;
        const cuttedEcgData = _.map(ecgData as any[], (subArr: any) =>
          subArr.slice(cuttedSampleStart, subArr.length - cuttedSampleStop),
        );
        const cuttedBeatSample: any[] = [];
        const cuttedHesBeatStatus: any[] = [];
        const cuttedBeatHeatMapIds: any[] = [];
        const cuttedBeatChannel: any[] = [];
        const cuttedBeatTemplateIds: any[] = [];
        if (!_.isEmpty(holterBeats?.beats)) {
          _.forEach(holterBeats.beats, (sample: any, index) => {
            const epochBeat =
              defaultStartStrip.valueOf() + (sample * 1000) / ecgDataMap.samplingFrequency;
            if (epochBeat >= startStripEpoch.current && epochBeat <= stopStripEpoch.current) {
              cuttedHesBeatStatus.push(holterBeats?.hesBeatStatus[index]);
              cuttedBeatChannel.push(holterBeats?.beatChannels[index]);
              cuttedBeatSample.push(
                Math.round(
                  ((epochBeat - startStripEpoch.current) / 1000) * ecgDataMap.samplingFrequency,
                ),
              );
              cuttedBeatHeatMapIds.push(holterBeats?.beatHeatMapIds[index]);
              cuttedBeatTemplateIds.push(holterBeats?.beatTemplateIds[index]);
            }
          });
        }
        // cuttedBeatTemplateIds is computed above but never included here - pre-existing bug;
        // cuttedHolterBeats.beatTemplateIds read below is always undefined.
        const cuttedHolterBeats: any = {
          beats: cuttedBeatSample,
          hesBeatStatus: cuttedHesBeatStatus,
          beatChannels: cuttedBeatChannel,
          beatHeatMapIds: cuttedBeatHeatMapIds,
        };
        // countBeatChannels returns a number but aiChannelState (Task 7) is typed TAiChannel | null - pre-existing.
        setAiChannel(countBeatChannels(cuttedHolterBeats?.beatChannels) as any);
        const eventsInfo = _.filter(events, (x) => x.type !== TYPE_EVENT_OTHER);
        const exportStart = startStripEpoch.current - 10000;
        const exportStop = stopStripEpoch.current + 10000;
        const ecgExport = await checkAndProcessEcgData({
          ecgDataMap,
          dataLength: ((exportStart - exportStop) / 1000) * ecgDataMap.samplingFrequency,
          momentObject: {
            startMoment: moment(exportStart),
            stopMoment: moment(exportStop),
          } as any,
        });
        createObjectEcg({
          ecg: cuttedEcgData,
          beats: cuttedHolterBeats?.beats,
          hesBeatStatus: cuttedHolterBeats?.hesBeatStatus,
          events: _.uniqWith(eventsInfo, (a, b) => {
            const aCopy = { id: a?.id, type: a?.type, idEvent: a?.idEvent };
            const bCopy = { id: b?.id, type: b?.type, idEvent: b?.idEvent };
            return _.isEqual(aCopy, bCopy);
          }),
          eventIndex,
          beatHeatMapIds: cuttedHolterBeats?.beatHeatMapIds,
          beatTemplateIds: cuttedHolterBeats?.beatTemplateIds,
          exportEcgData: {
            ecgData: ecgExport,
          },
        });
        originalBeatMarkers.current = cuttedHolterBeats?.beats;
        originalHesBeatStatus.current = cuttedHolterBeats?.hesBeatStatus;
        originalBeatHeatMapIds.current = cuttedHolterBeats?.beatHeatMapIds;
        originalBeatTemplateIds.current = cuttedHolterBeats?.beatTemplateIds;
        const hrsData = getChartHrsData();
        const prevMinute = minuteInfo.startTimeEpoch - 60000;
        const nextMinute = minuteInfo.startTimeEpoch + 60000;
        const prevMinuteHrData = _.find(hrsData, (x) => x.id === prevMinute);
        const nextMinuteHrData = _.find(hrsData, (x) => x.id === nextMinute);
        setIsDisablePrevBtn(!prevMinuteHrData?.avgHr);
        setIsDisableNextBtn(!nextMinuteHrData?.avgHr);
        setIsLoading(false);
        setIsLoadingEcgViewer(false);
      }
      isJumpToStart.current = false;
      isJumpToEnd.current = false;
    } catch (error) {
      if (!_.includes(error.message, 'canceled')) {
        logError('Failed to download ECG data: ', error);
      }
      setIsLoading(false);
      setIsLoadingEcgViewer(false);
    }
  };

  /**
   * Rebuilds the ECG viewer object so it picks up the current viewer configuration and beat edits. Regenerates beats and HES statuses from the originally downloaded data plus the pending beat and bulk changes, clones the existing object with the stored channel, filter, speed, amplitude and window settings, and does nothing when no object has been built yet.
   *
   * @returns Nothing.
   */
  const reCreateObjectEcg = () => {
    if (!_.isEmpty(ecgObject)) {
      const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
        beats: originalBeatMarkers.current,
        beatHeatMapIds: originalBeatHeatMapIds.current,
        hesBeatStatus: originalHesBeatStatus.current,
        beatTemplateIds: originalBeatTemplateIds.current,
        beatChanges: getBeatChanges(),
        startEpoch: startStripEpoch.current,
        stopEpoch: stopStripEpoch.current,
        applyBulkBeatsChanges: getBulkBeatsChanges(),
        resetAppliedBulkBeatsChanges: getResetBulkBeatsChanges(),
      });
      beatMarkersInEcgViewer.current = updatedBeats;
      hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;
      currentEcgViewerConfig.current = { ...ECG_VIEWER_CONFIG_STORE };
      const clonedEcgObject = _.cloneDeep(ecgObject);
      _.assign(clonedEcgObject, {
        hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
        hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
        notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
        speed: ECG_VIEWER_CONFIG_STORE.spdValue,
        amp: ECG_VIEWER_CONFIG_STORE.ampValue,
        wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
        aiPredict: [
          {
            beats: updatedBeats,
            hesBeatStatus: updatedHesBeatStatus,
            channel: 1,
            hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
          },
        ],
      });
      setEcgObject(clonedEcgObject);
      ecgObjectRef.current = clonedEcgObject;
    }
  };

  /**
   * Records the timestamp the technician clicked inside the ECG strip while a date pick is active. Converts the offset into an ISO time measured from the strip start and routes it to the picked start, end or bookmark date according to the active pick action, ignoring the click when no pick is in progress.
   *
   * @param diffSeconds - Seconds from the strip start to the clicked position.
   * @returns Nothing.
   */
  const handlePickDate = (diffSeconds: number) => {
    if (isPickingDate) {
      const date = moment(startStripEpoch.current).add(diffSeconds, 'seconds').toISOString();
      switch (pickDateAction) {
        case TPickDateAction.START: {
          setPickedStartDate(date);
          break;
        }
        case TPickDateAction.END: {
          setPickedEndDate(date);
          break;
        }
        case TPickDateAction.BOOKMARK: {
          setPickedBookmarkDate(date);
          break;
        }
        default: {
          break;
        }
      }
    }
  };

  /**
   * Records beat marker edits made inside the ECG viewer on the Beats tab. Diffs the incoming markers and HES statuses against the ones currently drawn, folds the result into the tab's beat changes, recomputes which applied bulk beat changes are now reset, and mirrors both into the beat options snapshot the undo and redo stack reads.
   *
   * @param updatedBeatObject - The viewer payload carrying currentBeatMarkers and currentHesBeatStatus.
   * @returns Nothing.
   */
  const handleUpdateBeat = (updatedBeatObject: any) => {
    const { currentBeatMarkers, currentHesBeatStatus } = updatedBeatObject;

    const updatedBeatMarkers = getUpdatedBeatMarkerV2(
      currentBeatMarkers,
      _.isEmpty(beatMarkersInEcgViewer.current)
        ? originalBeatMarkers.current
        : beatMarkersInEcgViewer.current,
      currentHesBeatStatus,
      _.isEmpty(hesBeatStatusInEcgViewer.current)
        ? originalHesBeatStatus.current
        : hesBeatStatusInEcgViewer.current,
      originalHesBeatStatus.current,
      startStripEpoch.current,
      originalBeatHeatMapIds.current,
      originalBeatMarkers.current,
    );

    beatMarkersInEcgViewer.current = currentBeatMarkers;
    hesBeatStatusInEcgViewer.current = currentHesBeatStatus;

    const resetBulkChanges = handleResetAppliedBuldBeats({
      appliedBuldBeats: getResetBulkBeatsChanges(),
      strips: updatedBeatMarkers,
      type: updatedBeatMarkers?.[0]?.type,
      bulkBeatsChanges: getBulkBeatsChanges(),
    });
    let beatsChanges: any[] = [];
    setBeatChanges((currBeatChange: any) => {
      const beats = handleUpdateBeatChanges(updatedBeatMarkers, currBeatChange);
      beatsChanges = beats;
      currentBeatsChangeRef.current = beats;
      return beats;
    });

    setResetBulkBeatsChanges(resetBulkChanges);
    setBeatOptions((prev: any) => ({
      ...prev,
      beatChangesState: beatsChanges,
      resetBulkBeatsChangesState: resetBulkChanges,
    }));
  };

  /**
   * Moves the ECG viewer to the previous or next minute of the study from the arrow buttons. Flags the upcoming redraw to jump to the start of the new strip when advancing and to its end when stepping back, then delegates to the wrapper's arrow handler prop.
   *
   * @param isNext - True to advance to the next minute, false to step back to the previous one.
   * @returns Nothing.
   */
  const clickArrowButtonHandle = useCallback((isNext: boolean) => {
    if (isNext) {
      isJumpToStart.current = true;
    } else {
      isJumpToEnd.current = true;
    }
    handleClickArrowButton(isNext);
  }, []);

  /**
   * Decides which capture mode the strip capture should use for the event currently selected on the Beats tab. Returns the artifact mode for artifact or deleted-beat events, the event mode for any other typed event, and the other mode when the selection carries no type.
   *
   * @returns The capture mode constant matching the selected event.
   */
  const getCaptureMode = () => {
    if (selectedEvent?.type) {
      if (
        selectedEvent.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT ||
        selectedEvent.type === TYPE_BEAT_ENUM.DELETED
      ) {
        return CAPTURE_MODE.ARTIFACT;
      }
      return CAPTURE_MODE.EVENT;
    }
    return CAPTURE_MODE.OTHER;
  };

  /**
   * Refreshes the ECG viewer object for capture, re-marking which event is selected and re-rendering the saved strip drawings. Rebuilds the event indicators from the saved events, takes the draw list from the pending change for the selected event or from the other-events that are not removed, regenerates beats from the original data, keeps the chart's current offset, and skips entirely when no saved events or cached object exist.
   *
   * @param config - Options carrying isUpdateEvents, which requires cached saved events before refreshing.
   * @returns Nothing.
   */
  const updateEcgCapture = (config?: { isUpdateEvents?: boolean }) => {
    const { isUpdateEvents = false } = config || {};
    if (isUpdateEvents && _.isEmpty(savedEvents.current)) {
      return;
    }
    if (_.isEmpty(ecgObjectRef.current)) return;
    const clonedEcgObject = _.cloneDeep(ecgObjectRef.current);
    const selectedEv = getSelectedEvent();
    const findEvent = _.find(savedEvents.current, (x) => x?.idEvent === selectedEv?.idEvent);
    const eventsInfo = _.map(savedEvents.current, (event) =>
      getEventInfoInEcgViewer({
        event,
        isSelected: findEvent?.idEvent === event?.idEvent,
        selectedStrip: findEvent,
      }),
    );

    const eventChanges = getEventChanges();
    const selectedEvent = getSelectedEvent();
    const selectedEventIndex = _.findIndex(
      eventChanges,
      (x) => x.idEvent === selectedEvent.idEvent,
    );
    const selectedEventChanges =
      selectedEventIndex > -1 ? eventChanges[selectedEventIndex] : selectedEvent;
    let drawList: any = [];
    if (selectedEventChanges?.type) {
      drawList = selectedEventChanges?.draw;
    } else {
      // isRemovedStrip is read here but IEventOther (Recoil/model.ts, Task 7) never declares it - pre-existing.
      const otherEvents = getEventOthers() as any[];
      const filterRemoveOther = _.filter(otherEvents, (x) => !x.isRemovedStrip);
      drawList =
        _.filter(_.flatten(_.map(filterRemoveOther, (event) => event.draw)), (x) => !!x) || [];
    }
    const { samplingFrequency } = ecgDataMap;
    const { draw } = formatDrawDisplay(
      drawList,
      startStripEpoch.current,
      samplingFrequency,
      stopStripEpoch.current,
    );
    const viewerObject = {
      options: {},
      widgets: draw,
    };
    const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
      beats: originalBeatMarkers.current,
      beatHeatMapIds: originalBeatHeatMapIds.current,
      hesBeatStatus: originalHesBeatStatus.current,
      beatTemplateIds: originalBeatTemplateIds.current,
      beatChanges: getBeatChanges(),
      startEpoch: startStripEpoch.current,
      stopEpoch: stopStripEpoch.current,
      applyBulkBeatsChanges: getBulkBeatsChanges(),
      resetAppliedBulkBeatsChanges: getResetBulkBeatsChanges(),
    });
    beatMarkersInEcgViewer.current = updatedBeats;
    hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;
    currentEcgViewerConfig.current = { ...ECG_VIEWER_CONFIG_STORE };
    _.assign(clonedEcgObject, {
      viewerObject,
      jumpOffset: ecgChartRef.current?.getOffsetX(),
      events: eventsInfo,
      hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
      hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
      notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
      speed: ECG_VIEWER_CONFIG_STORE.spdValue,
      amp: ECG_VIEWER_CONFIG_STORE.ampValue,
      wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
      aiPredict: [
        {
          beats: updatedBeats,
          hesBeatStatus: updatedHesBeatStatus,
          channel: 1,
          hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
        },
      ],
    });

    setEcgObject(clonedEcgObject);
    ecgObjectRef.current = clonedEcgObject;
  };
  useEffect(() => {
    updateEcgCapture({ isUpdateEvents: true });
  }, [selectedEvent.idEvent]);
  useEmitter(EMITTER_CONSTANTS.UPDATE_CAPTURE_EVENT, updateEcgCapture, [ecgObject]);

  const handleCaptureImages = ({
    draw,
    allIndexStripSvg,
    technicianComments,
    captureMode,
    captureTitle,
    visibleChannels,
  }: {
    draw: any;
    allIndexStripSvg: any;
    technicianComments: any;
    captureMode: any;
    captureTitle: any;
    visibleChannels: any;
  }) => {
    try {
      if (allIndexStripSvg.length > 0 && captureMode === CAPTURE_MODE.OTHER) {
        const { samplingFrequency } = ecgDataMap;
        const { formatDraw, stripStart, stripStop } = formatDrawMutation(
          draw,
          samplingFrequency,
          startStripEpoch.current,
          timezoneOffset,
          visibleChannels,
        );
        const startTime = stripStart;
        const endTime = stripStop;
        const data = handleAddEventOthersV2({
          events: getEventOthers(),
          draw: formatDraw,
          allIndexStripSvg,
          technicianComments,
          captureTitle,
          startTime,
          endTime,
          miniteInfo: getMinuteInfoEvents(),
        });
        // Perform grouping action applicable to undo/redo
        setEventOptions((prevOptions: any) => ({
          ...prevOptions,
          eventOthersState: data,
        }));
        setEventOthers(data);
      }
    } catch (error) {
      logError('Failed to capture images: ', error);
      toastrError(error.message, 'Error');
    }
  };

  useDeepCompareEffect(
    () => {
      isActiveQuery = true;
      if (isRenderViewerEcg) {
        downloadDataEcgV2(selectedColumnInfo, selectedEventIndex);
      }
    },
    [selectedColumnInfo, selectedEventIndex, reloadEcgViewer, isRenderViewerEcg],
    () => {
      isActiveQuery = false;
    },
  );

  /**
   * Redraws the ECG viewer after bookmarks, other-events or beats change while keeping the chart's current scroll position. Regenerates beats from the original data and pending changes, rebuilds the widget layer from the selected event's draw list or from the other-event strips, reuses the chart's current offset as the jump offset, and resets the chart's interaction mode when the redraw comes from beat edits.
   *
   * @param isChangeBeats - True when the redraw is caused by beat edits, which also resets the chart's interaction mode.
   * @returns Nothing.
   */
  const reDrawEcgObjectKeepPosition = (isChangeBeats?: boolean) => {
    if (!_.isEmpty(ecgObject)) {
      const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
        beats: originalBeatMarkers.current,
        hesBeatStatus: originalHesBeatStatus.current,
        beatHeatMapIds: originalBeatHeatMapIds.current,
        beatTemplateIds: originalBeatTemplateIds.current,
        beatChanges: getBeatChanges(),
        startEpoch: startStripEpoch.current,
        stopEpoch: stopStripEpoch.current,
        applyBulkBeatsChanges: getBulkBeatsChanges(),
        resetAppliedBulkBeatsChanges: getResetBulkBeatsChanges(),
      });
      beatMarkersInEcgViewer.current = updatedBeats;
      hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;

      currentEcgViewerConfig.current = { ...ECG_VIEWER_CONFIG_STORE };
      const clonedEcgObject = _.cloneDeep(ecgObjectRef.current);
      const { samplingFrequency } = ecgDataMap;
      const eventChanges = getEventChanges();
      const selectedEvent = getSelectedEvent();
      const selectedEventIndex = _.findIndex(
        eventChanges,
        (x) => x.idEvent === selectedEvent.idEvent,
      );
      const selectedEventChanges =
        selectedEventIndex > -1 ? eventChanges[selectedEventIndex] : selectedEvent;
      const otherEvents = getEventOthers();
      const drawList = selectedEventChanges?.type
        ? selectedEventChanges?.draw
        : _.filter(_.flatten(_.map(otherEvents, (event) => event.draw)), (x) => !!x) || [];
      const { draw } = formatDrawDisplay(
        drawList,
        startStripEpoch.current,
        samplingFrequency,
        stopStripEpoch.current,
      );

      const viewerObject = {
        options: {},
        widgets: draw,
      };

      _.assign(clonedEcgObject, {
        viewerObject,
        jumpOffset: ecgChartRef.current?.getOffsetX(),
        hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
        hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
        notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
        speed: ECG_VIEWER_CONFIG_STORE.spdValue,
        amp: ECG_VIEWER_CONFIG_STORE.ampValue,
        wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
        aiPredict: [
          {
            beats: updatedBeats,
            hesBeatStatus: updatedHesBeatStatus,
            channel: 1,
            hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
          },
        ],
      });
      if (isChangeBeats) {
        if (ecgChartRef.current) {
          ecgChartRef.current.resetInMode(true);
        }
      }
      setEcgObject(clonedEcgObject);
      ecgObjectRef.current = clonedEcgObject;
    }
  };

  // Re-create ECG object when beat changes
  useUpdateEffect(() => {
    if (isRenderViewerEcg) {
      reDrawEcgObjectKeepPosition();
    }
  }, [ecgBookmarks, eventOthers, isRenderViewerEcg]);

  useUpdateEffect(() => {
    if (!_.isEqual(beatChanges, currentBeatsChangeRef.current)) {
      currentBeatsChangeRef.current = beatChanges;
      reDrawEcgObjectKeepPosition(true);
    }
  }, [beatChanges]);

  useUpdateEffect(() => {
    if (isActiveTab) {
      if (!_.isEqual(currentEcgViewerConfig.current, ECG_VIEWER_CONFIG_STORE)) {
        reCreateObjectEcg();
      }
      if (ecgChartRef.current) {
        // resetInMode's declared signature (aiECGViewer.tsx, Task 5) requires isResetBeatAr; this
        // call site relies on the JS-era implicit-undefined-arg behaviour - pre-existing.
        (ecgChartRef.current as any).resetInMode();
      }
    } else {
      reCreateObjectEcg();
    }
  }, [isActiveTab]);

  useUpdateEffect(() => {
    /**
     * Scrolls the ECG chart to the requested jump time once the chart has finished loading. Converts the jump time into a sample position inside the current strip, calls the chart's jump with the stored portion and clears both jump values, and retries itself every 10 ms while the chart has not loaded, doing nothing when the time falls outside the strip.
     *
     * @returns Nothing.
     */
    const handleJumpToEcgViewer = () => {
      if (jumpTime <= stopStripEpoch.current && jumpTime >= startStripEpoch.current) {
        if (ecgChartRef.current?.hasLoadChart()) {
          // jumpTimeState (Recoil/index.ts, Task 7) is typed Number | undefined (boxed Number) - pre-existing gap.
          const jumpPos = Math.round(
            (((jumpTime as any) - startStripEpoch.current) / 1000) * ecgDataMap.samplingFrequency,
          );
          const jumpPortion = getJumpPortion();
          ecgChartRef.current.handleJumpTo(jumpPos, jumpPortion);
          setJumpPortion(0);
          setJumpTime(undefined);
        } else {
          setTimeout(() => {
            handleJumpToEcgViewer();
          }, 10);
        }
      }
    };
    if (
      jumpTime &&
      !_.isEmpty(ecgChartRef.current) &&
      startStripEpoch.current &&
      stopStripEpoch.current &&
      !isLoading
    ) {
      handleJumpToEcgViewer();
    }
  }, [
    jumpTime,
    isLoading,
    ecgObject, // trigger useEffect when data has cached
  ]);

  const eventIndicator = useMemo(() => {
    const { samplingFrequency } = ecgDataMap;
    let startPos = null;
    let endPos = null;
    if (pickedStartDate) {
      const pickedStartEpoch = new Date(pickedStartDate).getTime();
      startPos = Math.round(
        ((pickedStartEpoch - startStripEpoch.current) / 1000) * samplingFrequency,
      );
    }
    if (pickedEndDate) {
      const pickedEndEpoch = new Date(pickedEndDate).getTime();
      endPos = Math.round(((pickedEndEpoch - startStripEpoch.current) / 1000) * samplingFrequency);
    }
    return {
      startPos,
      endPos,
    };
  }, [pickedStartDate, pickedEndDate, ecgObject]);

  const bookmarksInRange = useMemo(() => {
    const result: any[] = [];
    _.forEach(ecgBookmarks, (bookmark: any) => {
      if (bookmark.center >= startStripEpoch.current && bookmark.center <= stopStripEpoch.current) {
        result.push(bookmark);
      }
    });
    return result;
  }, [ecgBookmarks, ecgObject]);

  const bookmarkIndicators = useMemo(() => {
    const { samplingFrequency } = ecgDataMap;
    const listBookmarks = [];
    if (pickedBookmarkDate) {
      const pickedEpoch = new Date(pickedBookmarkDate).getTime();
      listBookmarks.push({
        sample: Math.round(((pickedEpoch - startStripEpoch.current) / 1000) * samplingFrequency),
        id: 'marker',
      });
    }
    _.forEach(bookmarksInRange, (bookmark) => {
      listBookmarks.push({
        sample: Math.round(
          ((bookmark.center - startStripEpoch.current) / 1000) * samplingFrequency,
        ),
        id: bookmark.center,
      });
    });
    return listBookmarks;
  }, [pickedBookmarkDate, bookmarksInRange, ecgObject]);

  useEffect(() => {
    /**
     * Cancels an in-progress date pick when the technician presses Escape. Ignores auto-repeat key events and resets the pick action to none only while a pick action is set.
     *
     * @param event - The keyup event delivered by the window listener.
     * @returns Nothing.
     */
    const handleKeyup = (event: any) => {
      if (event?.repeat) {
        return;
      }
      const { key } = event;
      if (key === 'Escape' && pickDateAction) {
        setPickDateAction(TPickDateAction.NONE);
      }
    };
    if (isPickingDate) {
      window.addEventListener('keyup', handleKeyup);
    }
    return () => {
      window.removeEventListener('keyup', handleKeyup);
    };
  }, [pickDateAction, isPickingDate]);

  return {
    isLoading,
    isLoadingStrip,
    ecgObject,
    startStripEpoch,
    ecgChartRef,
    aiChannel,
    isDisableNextBtn,
    isDisablePrevBtn,
    pickDateAction,
    eventIndicator,
    bookmarkIndicators,
    isPickingDate,
    selectedEvent,
    getCaptureMode,
    handleCaptureImages,
    handlePickDate,
    clickArrowButtonHandle,
    handleUpdateBeat,
  };
};

export default useEventEcgViewerWrapper;
